import { Injectable } from '@angular/core';
import { IStatistic2ReportParameter, IStatistic2Report } from '@fi-app/statistic2/statistic2';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { AccessRightFilterCollectionModel, ILightWeightObject, ILightWeightObjectCollection } from '@fi-shared/models';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { BehaviorSubject, Subject } from 'rxjs';
import { Statistic2ReportParameterModel } from '@fi-app/statistic2/statistic2-report-parameter-list/statistic2-report-parameter-model';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';

interface IDashBoardItemData {
    reportParameter: IStatistic2ReportParameter;
    reportData?: IStatistic2Report;
}

@Injectable()
export class Statistic2Service {
    reportParametersUpdated$ = new Subject<IStatistic2ReportParameter[]>();
    dashboardReports$: BehaviorSubject<IDashBoardItemData[]> = new BehaviorSubject<IDashBoardItemData[]>(null);
    reportParameterDashboardAdded = new Subject<void>();

    constructor(
        private fiBackend: FiBackendService,
        private accessRightService: AccessRightService,
        private fiLoading: LoadingService,
    ) {
        this.initDashboardItems();
    }

    getReportParametersListData(parameter: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<any>>(
            'Statistic/Statistic2/GetReportParametersListData',
            parameter,
        );
    }

    getReportParameters(parameter: Statistic2ReportParameterModel) {
        return this.fiBackend.fetchDataPost<IStatistic2ReportParameter[]>(
            'Statistic/Statistic2/GetReportParameters',
            parameter,
        );
    }

    getReportParametersCount(parameter: Statistic2ReportParameterModel) {
        return this.fiBackend.fetchDataPost<number>('Statistic/Statistic2/GetReportParametersCount', parameter);
    }

    getReportParameter(id: number) {
        return this.fiBackend.fetchData<IStatistic2ReportParameter>('Statistic/Statistic2/GetReportParameter/' + id);
    }

    getLightWeightObjectsForLocalSearch(parameter: Statistic2ReportParameterModel) {
        return this.fiBackend.fetchDataPost<ILightWeightObjectCollection>(
            'Statistic/Statistic2/GetLightWeightObjectsForLocalSearch',
            parameter,
        );
    }

    deleteReportParameters(reportParametersToDelete: IStatistic2ReportParameter[]) {
        const reportParameterIds = reportParametersToDelete.map((rp) => rp.Id);
        return this.fiBackend
            .fetchDataPost('Statistic/Statistic2/DeleteReportParameters', reportParameterIds)
            .then(() => {
                const reportParametersUpdatedData = reportParametersToDelete.map((rp) =>
                    Object.assign(rp, { ShowOnDashboard: false }),
                );
                this.reportParametersUpdated$.next(reportParametersUpdatedData);
            });
    }

    saveReportParameter(reportParameter: IStatistic2ReportParameter, skipReportParameterUpdatedEvent?: boolean) {
        return this.fiBackend
            .fetchDataPost<number>('Statistic/Statistic2/SaveReportParameter', reportParameter)
            .then((id) => {
                if (!skipReportParameterUpdatedEvent) {
                    const isNew = !reportParameter.Id;
                    reportParameter.Id = id;
                    // https://chainformation.atlassian.net/browse/FI-3266
                    if (isNew) {
                        this.getReportParameter(id).then((newReportParameter) =>
                            this.reportParametersUpdated$.next([newReportParameter]),
                        );
                    } else {
                        this.reportParametersUpdated$.next([reportParameter]);
                    }
                }
            });
    }

    getReportsData(reportParameters: IStatistic2ReportParameter[]) {
        return this.fiBackend.fetchDataPost<IStatistic2Report[]>(
            'Statistic/Statistic2/GetReportsData',
            reportParameters,
        );
    }

    getReportData(reportParameter: IStatistic2ReportParameter, extendedReport?: boolean) {
        return this.fiBackend.fetchDataPost<IStatistic2Report>(
            `Statistic/Statistic2/GetReportData?extendedReport=${!!extendedReport}`,
            reportParameter,
        );
    }

    getDetailedReportByUserData(reportParameter: IStatistic2ReportParameter) {
        return this.fiBackend.fetchDataPost<IStatistic2Report>(
            'Statistic/Statistic2/GetDetailedReportByUser',
            reportParameter,
        );
    }

    getDetailedReportDocumentVisitsByUser(reportParameter: IStatistic2ReportParameter) {
        return this.fiBackend.fetchDataPost<IStatistic2Report>(
            'Statistic/Statistic2/GetDetailedReportDocumentVisitsByUser',
            reportParameter,
        );
    }

    getDetailedReportNoGroupingData(reportParameter: IStatistic2ReportParameter) {
        return this.fiBackend.fetchDataPost<IStatistic2Report>(
            'Statistic/Statistic2/GetDetailedReportNoGrouping',
            reportParameter,
        );
    }

    getDataForReportParameterSettings() {
        return this.fiBackend.fetchData<{
            AccessRights: AccessRightFilterCollectionModel;
            Documents: ILightWeightObject[];
            DocumentTags: ILightWeightObject[];
            MediaBankFiles: ILightWeightObject[];
            MediaBankFileTags: ILightWeightObject[];
        }>('Statistic/Statistic2/GetDataForReportParameterSettings');
    }

    loadDashboardItems() {
        this.dashboardReports$.next(null);
        return this.fiLoading
            .trackPromise(this.getReportParameters(new Statistic2ReportParameterModel(true)))
            .then((reportParameters) => {
                const initialReports = reportParameters.map((reportParameter) => ({
                    reportParameter: reportParameter,
                    reportData: null,
                }));
                // load reports data by 2 items in row
                let i = 0;
                const chunk = 2;
                const loadDataFunc = () => {
                    if (i < initialReports.length) {
                        let currReports = initialReports.slice(i, i + chunk);
                        this.loadReportsData(currReports, () => {
                            i += chunk;
                            loadDataFunc();
                        });
                    }
                };
                loadDataFunc();
                this.dashboardReports$.next(initialReports);
            });
    }

    hasReportParameters() {
        return this.fiBackend.fetchData<boolean>('Statistic/Statistic2/HasReportParameters');
    }

    generateDefaultReportParameters() {
        return this.fiBackend.fetchDataPost<void>('Statistic/Statistic2/GenerateDefaultReportParameters');
    }

    private initDashboardItems() {
        this.reportParametersUpdated$.subscribe((reportParameters) => {
            if (!this.dashboardReports$.value) return;
            reportParameters.forEach((rp) => {
                const existingReport = this.dashboardReports$.value.find((item) => item.reportParameter.Id === rp.Id);
                if (!rp.ShowOnDashboard) {
                    if (existingReport) {
                        // item was removed from dashboard
                        this.dashboardReports$.value.splice(this.dashboardReports$.value.indexOf(existingReport), 1);
                        this.dashboardReports$.next(this.dashboardReports$.value);
                    }
                } else {
                    const newReport: IDashBoardItemData = {
                        reportParameter: rp,
                    };
                    if (!existingReport) {
                        // item is new on dashboard
                        this.dashboardReports$.value.push(newReport);
                        setTimeout(() => {
                            this.reportParameterDashboardAdded.next();
                        });
                    } else {
                        // item exists on dashboard, reload
                        this.dashboardReports$.value[this.dashboardReports$.value.indexOf(existingReport)] = newReport;
                    }
                    this.dashboardReports$.next(this.dashboardReports$.value);
                    this.loadReportsData([newReport]);
                }
            });
        });
    }

    private loadReportsData(currReports: IDashBoardItemData[], callback?: () => void) {
        this.getReportsData(currReports.map((item) => item.reportParameter))
            .then((reports) => {
                // fill current reports by its data
                for (let j = 0; j < reports.length; j++) {
                    currReports[j].reportData = reports[j];
                }
                this.dashboardReports$.next(this.dashboardReports$.value);
            })
            .finally(() => {
                if (callback) {
                    callback();
                }
            });
    }
}
